/**
 * OnboardingLoadingShell
 *
 * Renders the onboarding chrome (brand, step rail, step header, nav footer)
 * with a skeleton step body — used as `loadingComponent` for
 * `AuthenticatedRoute` so the token/auth handshake shows the real onboarding
 * layout instead of the generic `LoadingFallback` (which is admin-flavored).
 *
 * Cannot reuse `OnboardingFlow` directly here because that component wires
 * up mutation hooks that depend on the authenticated API client; this shell
 * runs *before* auth completes and must therefore be hook-free.
 */

import { PrivacyLinks } from "@order-up/common";
import { Sparkles } from "lucide-react";
import { STEP_CONFIG, STEP_ORDER } from "../types/onboarding";
import { StepBodySkeleton } from "./StepBodySkeleton";
import { MobileStepIndicator } from "./ui/MobileStepIndicator";
import { StepNavigation } from "./ui/StepNavigation";
import { StepRail, type StepRailItem } from "./ui/StepRail";

const TOTAL_STEPS = STEP_ORDER.length;

const STEP_RAIL_ITEMS: StepRailItem[] = STEP_ORDER.map((key, index) => ({
  index,
  label: STEP_CONFIG[key].label,
  description: STEP_CONFIG[key].description,
}));

const NOOP = () => {};

export function OnboardingLoadingShell() {
  const currentStepIndex = 0;
  const currentStepKey = STEP_ORDER[currentStepIndex];
  const currentStepConfig = STEP_CONFIG[currentStepKey];

  return (
    // The auth chain (TokenHandler → AuthGuard → AppContent → OnboardingApp)
    // can remount this shell several times in succession as each layer's
    // loading state resolves. Each remount creates a new DOM tree, which
    // pre-W10 looked like the page was "refreshing 3+ times" to the user.
    // A short fade-in on the wrapper smooths those serial remounts —
    // identical content fading in feels stable; identical content snapping
    // in feels like reloads.
    <div className="flex h-dvh w-full overflow-hidden bg-background text-foreground animate-in fade-in duration-150">
      <aside
        aria-label="Application progress"
        className="hidden lg:flex w-[320px] shrink-0 flex-col border-r border-white/5 bg-linear-to-b from-zinc-950 via-background to-background"
      >
        <div className="flex items-center gap-3 px-6 pt-8 pb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-linear-to-br from-primary to-blue-400 shadow-[0_0_30px_rgba(59,130,246,0.35)]">
            <Sparkles className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex min-w-0 flex-col">
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
              OrderUp
            </span>
            <span className="truncate text-base font-semibold text-white">
              Onboarding Process
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4">
          <StepRail
            items={STEP_RAIL_ITEMS}
            currentIndex={currentStepIndex}
            onNavigate={NOOP}
            disabled
          />
        </div>

        <div className="border-t border-white/5 px-6 py-5">
          <p className="text-[11px] leading-relaxed text-muted">
            Need help filling this out? Email{" "}
            <a
              href="mailto:support@letsorderup.com"
              className="text-zinc-300 underline-offset-2 hover:text-white hover:underline"
            >
              support@letsorderup.com
            </a>
            .
          </p>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col">
        <div className="lg:hidden">
          <MobileStepIndicator
            current={currentStepIndex + 1}
            total={TOTAL_STEPS}
            currentLabel={currentStepConfig?.label}
          />
        </div>

        <header className="hidden lg:block shrink-0 border-b border-white/5 px-12 pt-10 pb-8">
          <div className="mx-auto max-w-2xl">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted">
              <span>Step {currentStepIndex + 1}</span>
              <span className="h-px w-6 bg-border" />
              <span>of {TOTAL_STEPS}</span>
            </div>
            <h1 className="mt-3 text-3xl font-semibold leading-tight text-white">
              {currentStepConfig?.label}
            </h1>
            {currentStepConfig?.description && (
              <p className="mt-2 text-base text-muted">
                {currentStepConfig.description}
              </p>
            )}
          </div>
        </header>

        <div
          tabIndex={0}
          role="region"
          aria-label="Onboarding step"
          className="flex-1 overflow-y-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
        >
          <div className="mx-auto max-w-2xl px-6 py-8 lg:px-12 lg:py-10">
            <div className="mb-6 lg:hidden">
              <h1 className="text-2xl font-semibold leading-tight text-white">
                {currentStepConfig?.label}
              </h1>
              {currentStepConfig?.description && (
                <p className="mt-1 text-sm text-muted">
                  {currentStepConfig.description}
                </p>
              )}
            </div>

            <StepBodySkeleton stepIndex={currentStepIndex} />
          </div>
        </div>

        <footer className="shrink-0 border-t border-white/5 bg-background/80 backdrop-blur-xl">
          <div className="mx-auto max-w-2xl px-6 py-4 lg:px-12 lg:py-5">
            <StepNavigation
              onBack={NOOP}
              onNext={NOOP}
              canProceed={false}
              isSubmitting={false}
              isFirst
              isLast={false}
            />
            {/* Onboarding collects the owner's identity, business and bank
                details, so every step links the policy (SOC 2 R39). */}
            <PrivacyLinks className="mt-3 justify-center" linkClassName="text-xs text-muted hover:text-foreground" />
          </div>
        </footer>
      </main>
    </div>
  );
}
